/**
 * LeaderboardPage — Local area quest leaderboard
 * Route: /leaderboard
 */
import { useState, useEffect } from 'react';
import { Trophy, Medal, Flame, ArrowLeft, MapPin } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLocation } from '../contexts/LocationContext';
import { Avatar, AvatarImage, AvatarFallback } from '../components/ui/avatar';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const RANK_STYLES = [
  { bg: 'bg-gradient-to-r from-[#F4C430] to-[#FF8C42]', text: 'text-white', badge: '1st' },
  { bg: 'bg-gray-200', text: 'text-gray-800', badge: '2nd' },
  { bg: 'bg-amber-100', text: 'text-amber-800', badge: '3rd' },
];

const LeaderboardPage = () => {
  const { locationString, location: loc } = useLocation();
  const city = loc?.city || locationString?.split(',')[0] || 'your area';
  const [leaders, setLeaders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API_URL}/api/quests/leaderboard?city=${encodeURIComponent(city)}&limit=20`)
      .then(r => r.json())
      .then(d => setLeaders(d.leaders || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [city]);

  return (
    <div className="min-h-screen bg-[#FAFAFA] pb-20 md:pb-8">
      {/* Hero */}
      <div className="relative" style={{ minHeight: '200px' }}>
        <div className="absolute inset-0 bg-gradient-to-br from-[#FF6B35] via-[#FF8C42] to-[#F4C430]" />
        <div className="relative flex flex-col justify-end max-w-3xl mx-auto px-4 sm:px-6" style={{ minHeight: '200px' }}>
          <div className="mb-6">
            <Link to="/" className="inline-flex items-center gap-1 text-white/70 hover:text-white text-xs mb-3 transition-colors">
              <ArrowLeft className="w-3 h-3" /> Back
            </Link>
            <div className="flex items-center gap-3">
              <Trophy className="w-10 h-10 text-white" />
              <div>
                <h1 className="text-2xl md:text-3xl font-bold text-white">Quest Leaderboard</h1>
                <p className="text-white/70 text-sm flex items-center gap-1 mt-0.5"><MapPin className="w-3 h-3" />Most Verified in {city}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 py-6">
        {loading ? (
          <div className="space-y-3">{[1,2,3,4,5].map(i => <div key={i} className="h-16 bg-gray-100 rounded-2xl animate-pulse" />)}</div>
        ) : leaders.length === 0 ? (
          <div className="text-center py-16">
            <Medal className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-gray-700 mb-2">No verified quests yet</h3>
            <p className="text-sm text-gray-500">Be the first to verify a quest and claim the top spot!</p>
          </div>
        ) : (
          <div className="space-y-2">
            {leaders.map((leader, i) => {
              const style = RANK_STYLES[i] || { bg: 'bg-white', text: 'text-gray-900', badge: `${i + 1}` };
              return (
                <div key={leader.user_id || i} className={`flex items-center gap-4 p-4 rounded-2xl ${i < 3 ? style.bg : 'bg-white'} ${i < 3 ? '' : 'shadow-[0_1px_3px_rgba(0,0,0,0.04)]'} transition-all`}>
                  <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-black ${i < 3 ? style.text : 'text-gray-500 bg-gray-100'}`}>
                    {i < 3 ? <Medal className="w-5 h-5" /> : style.badge}
                  </div>
                  <Avatar className="w-10 h-10">
                    <AvatarImage src={leader.user_avatar || `https://i.pravatar.cc/150?u=${leader.user_id}`} />
                    <AvatarFallback className="bg-gradient-to-br from-[#F4C430] to-[#FF6B35] text-white text-xs font-bold">
                      {(leader.user_name || 'F')[0]}
                    </AvatarFallback>
                  </Avatar>
                  <div className="flex-1 min-w-0">
                    <p className={`text-sm font-bold ${i < 3 ? style.text : 'text-gray-900'}`}>{leader.user_name || 'Foodie'}</p>
                    <p className={`text-xs ${i < 3 ? `${style.text} opacity-70` : 'text-gray-500'}`}>{leader.verified_count} verified quests</p>
                  </div>
                  <div className={`flex items-center gap-1 ${i < 3 ? style.text : 'text-[#FF6B35]'}`}>
                    <Flame className="w-4 h-4" />
                    <span className="text-sm font-bold">{leader.verified_count}</span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default LeaderboardPage;
